import { type SECRIntensity } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { cn } from '@shared/lib/utils';

interface SECRReportIntensityTableProps {
  intensity: SECRIntensity | null | undefined;
  prevYearLabel: string;
  currentYearLabel: string;
}

const fmt = (n: number | null | undefined) => {
  if (n == null || isNaN(n)) return '-';
  const digits = n > 0 && n < 0.01 ? 4 : 2;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

function pctChange(
  current: number | null | undefined,
  prior: number | null | undefined
) {
  if (current == null || prior == null || prior === 0) return null;
  return ((current - prior) / prior) * 100;
}

export function SECRReportIntensityTable({
  intensity,
  prevYearLabel,
  currentYearLabel,
}: SECRReportIntensityTableProps) {
  const hasPriorYear = intensity?.priorYearValue != null;
  const label = intensity?.label ?? 'Intensity ratio';
  const unit = intensity?.unit ?? '';
  const change = pctChange(intensity?.value, intensity?.priorYearValue);

  return (
    <div className="overflow-hidden rounded-lg border shadow-sm">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-muted/50 border-b">
            <th className="p-3 text-left text-sm font-semibold">Metric</th>
            {hasPriorYear && (
              <th className="border-l p-3 text-right text-sm font-semibold">
                {prevYearLabel}
              </th>
            )}
            <th
              className={cn(
                'border-l p-3 text-right text-sm font-semibold',
                hasPriorYear && 'bg-muted/40'
              )}
            >
              {currentYearLabel}
            </th>
            {hasPriorYear && (
              <th className="border-l p-3 text-right text-sm font-semibold">
                % Change
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr className="hover:bg-muted/20 border-b transition-colors last:border-0">
            <td className="px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Icons.barChart2 className="size-3.5 text-orange-400" />
                <span className="text-sm">{label}</span>
                {unit && (
                  <span className="text-xs text-muted-foreground">
                    tCO₂e&nbsp;/&nbsp;{unit}
                  </span>
                )}
              </div>
            </td>
            {hasPriorYear && (
              <td className="border-l px-3 py-2.5 text-right text-sm tabular-nums">
                {fmt(intensity?.priorYearValue)}
              </td>
            )}
            <td
              className={cn(
                'border-l px-3 py-2.5 text-right text-sm font-medium tabular-nums',
                hasPriorYear && 'bg-muted/30'
              )}
            >
              {fmt(intensity?.value)}
            </td>
            {hasPriorYear && (
              <td className="border-l px-3 py-2.5 text-right">
                {change == null ? (
                  <span className="text-muted-foreground">-</span>
                ) : (
                  <span
                    className={cn(
                      'inline-flex items-center gap-0.5 text-xs font-medium',
                      change < 0 ? 'text-teal-600' : 'text-orange-500'
                    )}
                  >
                    {change < 0 ? (
                      <Icons.arrowDown className="size-3" />
                    ) : (
                      <Icons.arrowUp className="size-3" />
                    )}
                    {Math.abs(change).toFixed(1)}%
                  </span>
                )}
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
